<script setup lang="ts">
import { computed, toRef, watch, type ComputedRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useField, useIsFormDirty } from 'vee-validate';

import LocalizedEditor from '@/components/base/LocalizedEditor.vue';
import LocalizedSelect from '@/components/base/LocalizedSelect.vue';
import LocalizedVideo from '../base/LocalizedVideo.vue';
import ImageField from '@/components/ImageField.vue';
import LocalizedInput from '@/components/LocalizedInput.vue';
import StandardToggle from '../StandardToggle.vue';
import ColorPicker from '../ColorPicker.vue';

import { formSaveImageGuard } from '@/helpers/form.helper';
import type { Locales, ProjectModuleDto, ProjectModuleHeroConfigDto, ThemeHost } from '@/services/types/api.types';
import { useBlocksStore } from '@/stores';
import type { SavingOptions } from '@/services/types/file-uploader.types';
import type { FileInfo } from '@/services/types/file.types';
import { useTheme } from '@/composables/useTheme';
import { ModuleTypeEnum } from '@/types/modules';

const blocksStore = useBlocksStore();

// CONST
const { t } = useI18n();

// PROPS
const props = defineProps<{
  languageEnabled: Locales[];
  editLocale: Locales;
  module: ProjectModuleDto;
  themeKey: string;
  themeHost: ThemeHost;
}>();

const emit = defineEmits<{
  (e: 'saveImage', options: SavingOptions): void;
  (e: 'deleteImage'): void;
}>();

// COMPUTED
const moduleConfig: ComputedRef<ProjectModuleHeroConfigDto> = computed(
  () => (props.module.config as ProjectModuleHeroConfigDto) || ({} as ProjectModuleHeroConfigDto),
);

const isDirty = useIsFormDirty();

const { value: displayMode } = useField<'image' | 'video'>('config.displayMode');
const { value: useProjectTitle } = useField<boolean>('config.useProjectTitle');
const { value: useProjectImage } = useField<boolean>('config.useProjectImage');
const { value: backgroundOpacity } = useField<number>('config.overlay.backgroundOpacity');
const { value: layout } = useField<number>('config.layout');
const { value: image } = useField<FileInfo | null>('config.image');

// The project toggles take a Hero's title and image from its project and list it in the project's table of contents,
// so they show only on a project. On a page, ModuleDetailView loads them off.
const isProjectBlock = computed(() => props.themeHost === 'project');

// ImageField shows the form's image. On a project that is the stored one: its image route stores it at once, and the
// view resets the form to the block it refetches, which would discard unsaved edits, so ImageField asks first. On a
// page the image is a form field like the others, and nothing discards the edits.
const imageFormDirty = computed(() => isProjectBlock.value && isDirty.value);

const themeConfiguration = useTheme(toRef(props, 'themeHost'), toRef(props, 'themeKey'));
const layoutOptions = themeConfiguration.getLayoutOptions(ModuleTypeEnum.Hero);
const cropping = themeConfiguration.getCroppingInfo(ModuleTypeEnum.Hero, layout);
const alignmentOptions = computed(() => blocksStore.getVideoAlignmentOptions());

// FUNCTIONS
const handleSaveImage = async (options: SavingOptions) => {
  emit('saveImage', options);
};

function removeImage() {
  emit('deleteImage');
}

const handleClearImage = async () => {
  formSaveImageGuard(false, removeImage);
};

watch(useProjectImage, (newValue) => {
  if (newValue) image.value = null;
});
</script>

<template>
  <div>
    <standard-toggle
      v-if="isProjectBlock"
      name="config.useProjectTitle"
      :label="t('project-module.hero.useProjectTitle')"
      outlined
    />

    <div v-if="!useProjectTitle">
      <localized-input
        name="title"
        :label="t('global.label.title')"
        :editLocale="editLocale"
        outlined
        :language_enabled="props.languageEnabled"
        class="q-mt-md"
      />
    </div>

    <div v-if="!useProjectTitle">
      <q-field borderless :label="t('global.label.content')" stack-label>
        <template v-slot:control>
          <localized-editor
            name="config.overlay.description"
            :label="t('project-module.hero.overlay.description')"
            class="q-mt-sm full-width"
            :editLocale="editLocale"
            :language_enabled="props.languageEnabled"
          />
        </template>
      </q-field>
    </div>

    <q-select
      v-model="layout"
      :label="t('project-module.hero.layout.label')"
      :options="layoutOptions"
      option-value="value"
      option-label="label"
      emit-value
      map-options
      outlined
      class="q-mt-md"
    >
      <template v-slot:option="scope">
        <q-item v-bind="scope.itemProps">
          <q-item-section>
            <q-item-label>{{ scope.opt.label }}</q-item-label>
            <q-item-label caption>{{ scope.opt.description }}</q-item-label>
          </q-item-section>
        </q-item>
      </template>
    </q-select>

    <localized-select
      name="config.overlay.alignment"
      :label="t('project-module.hero.overlay.textAlignment')"
      :editLocale="editLocale"
      :options="alignmentOptions"
      emit-value
      outlined
      map-options
      class="q-mt-md"
    />

    <standard-toggle
      v-if="isProjectBlock"
      name="config.includeInTableOfContents"
      :label="t('project-module.admin.includeInTableOfContents')"
      outlined
    />

    <div class="q-mb-lg">
      <q-separator spaced="lg" />
      <h2>{{ t('project-module.hero.background.title') }}</h2>
    </div>

    <!-- Display Mode Selector -->
    <!-- <localized-select
      name="config.displayMode"
      :label="t('project-module.hero.displayMode')"
      :editLocale="editLocale"
      :options="displayModeOptions"
      emit-value
      outlined
      map-options
      class="q-mt-sm"
      disabled
    /> -->

    <div v-if="displayMode === 'image'">
      <standard-toggle
        v-if="isProjectBlock"
        name="config.useProjectImage"
        :label="t('project-module.hero.useProjectImage')"
        outlined
        class="q-mb-sm"
      />
      <div v-if="!useProjectImage">
        <image-field
          :label="t('form.question.image.label')"
          version="v2"
          @clear-image="handleClearImage"
          @save-image="handleSaveImage"
          :form-dirty="imageFormDirty"
          :language-enabled="props.languageEnabled"
          :cropping="cropping"
          :file="image"
        />
      </div>
    </div>

    <div class="q-mt-sm" v-else-if="displayMode === 'video'">
      <localized-video
        name="config.video"
        :label="t('form.dialogs.module.video.url')"
        :language_enabled="props.languageEnabled"
        :caption="moduleConfig.videoCaption"
        :disableContent="true"
        :disableCaption="true"
      />
    </div>

    <!-- Text Color -->
    <div class="row">
      <div class="col-6 q-pr-sm">
        <color-picker
          color="config.overlay.backgroundColor"
          :label="t('project-module.hero.overlay.backgroundColor')"
        />
      </div>
      <div class="col-6">
        <color-picker color="config.overlay.textColor" :label="t('project-module.hero.overlay.textColor')" />
      </div>
    </div>

    <!-- Overlay Settings -->
    <div class="row q-mt-md">
      <div class="col-6">
        <q-item>
          <q-item-section avatar>
            <q-icon name="opacity" />
          </q-item-section>
          <q-item-section>
            <q-item-label>{{ t('project-module.hero.overlay.backgroundOpacity') }}</q-item-label>
            <q-slider v-model.number="backgroundOpacity" :min="0" :max="1" :step="0.1" label color="primary" />
          </q-item-section>
        </q-item>
      </div>
    </div>
  </div>
</template>
